import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, CircularProgress, Button } from "@mui/material";
import { Layout } from "../../components/layout";
import { Search } from "../../components/Search";
import { Select } from "../../components/Select";
import { DateInput } from "../../components/DateInput";
import DataTable, { ColumnConfig } from "../../components/DataTable";
import Tabs, { Tab } from "../../components/Tabs";
import StatusBadge from "../../components/StatusBadge";
import {
  auditLogsService,
  AuditLogEntry,
  AuditLogCategory,
  GetAuditLogsParams,
} from "../../services/auditLogsService";
import { AuditLogsPageStyled } from "./AuditLogsPage.styled";

// Tab definitions
const TABS: Tab<string>[] = [
  { id: "all", label: "All Logs" },
  { id: "user_management", label: "User Management" },
  {
    id: "patient_treatment_plan",
    label: "Patient and Treatment Plan Activity",
  },
];

// Map tab ID to category
const TAB_CATEGORY_MAP: Record<string, AuditLogCategory | undefined> = {
  all: undefined,
  user_management: "user_management",
  patient_treatment_plan: "patient_treatment_plan",
};

// Role options for filter
const ROLE_OPTIONS = [
  { value: "", label: "All Roles" },
  { value: "provider", label: "Provider" },
  { value: "assistant", label: "Assistant" },
  { value: "manager", label: "Manager" },
];

const ITEMS_PER_PAGE = 25;

interface AuditLogRow extends Record<string, unknown> {
  id: number;
  userName: string;
  timestamp: string;
  userRole: string;
  actionDescription: string;
  status: "success" | "failure";
  description: string;
}

const AuditLogsPage: React.FC = () => {
  const { t } = useTranslation();

  // State
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRangeError, setDateRangeError] = useState<string | null>(null);
  const [sortField, setSortField] = useState<string>("timestamp");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Validate date range
  const isDateRangeValid = useMemo(() => {
    if (dateFrom && dateTo) {
      return new Date(dateFrom) <= new Date(dateTo);
    }
    return true;
  }, [dateFrom, dateTo]);

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    if (!isDateRangeValid) return;
    setLoading(true);
    try {
      const params: GetAuditLogsParams = {
        search: debouncedSearch || undefined,
        category: TAB_CATEGORY_MAP[activeTab],
        role: roleFilter || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        start: (currentPage - 1) * ITEMS_PER_PAGE,
        limit: ITEMS_PER_PAGE,
        orderBy: sortField,
        order: sortDirection,
      };

      const response = await auditLogsService.getAuditLogs(params);
      setLogs(response.data);
      setTotalLogs(response.total);
      setError(null);
    } catch (error) {
      console.error("Error fetching audit logs:", error);
      setLogs([]);
      setTotalLogs(0);
      setError(
        t(
          "auditLogsPage.loadError",
          "Failed to load audit logs. Please try again.",
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [
    activeTab,
    debouncedSearch,
    roleFilter,
    dateFrom,
    dateTo,
    currentPage,
    sortField,
    sortDirection,
    isDateRangeValid,
    t,
  ]);

  // Initial fetch and refetch on filter changes
  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Validate date range and show/hide error
  useEffect(() => {
    if (dateFrom && dateTo && new Date(dateFrom) > new Date(dateTo)) {
      setDateRangeError(
        t("auditLogsPage.dateRangeError", '"From Date" must be earlier than or equal to "To Date"'),
      );
    } else {
      setDateRangeError(null);
    }
  }, [dateFrom, dateTo, t]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, debouncedSearch, roleFilter, dateFrom, dateTo]);

  // Handle tab change
  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  // Handle search
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Handle role filter
  const handleRoleFilterChange = (
    value: string | number | (string | number)[],
  ) => {
    setRoleFilter(String(value || ""));
  };

  // Handle date filters
  const handleDateFromChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setDateFrom(e.target.value);
  };

  const handleDateToChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setDateTo(e.target.value);
  };

  // Handle sort
  const handleSort = (field: string, direction: "asc" | "desc") => {
    setSortField(field);
    setSortDirection(direction);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Format timestamp
  const formatTimestamp = useCallback((timestamp: string) => {
    const date = new Date(timestamp);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;
    const timeStr = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "UTC",
    });
    return { date: dateStr, time: timeStr };
  }, []);

  // Map logs to row data
  const logRows: AuditLogRow[] = useMemo(
    () =>
      logs.map((log) => ({
        id: log.id,
        userName: log.userName,
        timestamp: log.timestamp,
        userRole: log.userRole,
        actionDescription: log.actionDescription,
        status: log.status,
        description: log.description,
      })),
    [logs],
  );

  // Columns configuration
  const columns: ColumnConfig<AuditLogRow>[] = useMemo(
    () => [
      {
        key: "userName",
        title: t("auditLogsPage.columns.user", "User"),
        sortable: true,
        width: "170px",
        render: (value: unknown) => (
          <Typography className="clickable">{String(value)}</Typography>
        ),
      },
      {
        key: "timestamp",
        title: t("auditLogsPage.columns.timestamp", "Timestamp"),
        sortable: true,
        width: "132px",
        render: (value: unknown) => {
          const { date, time } = formatTimestamp(String(value));
          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <Typography sx={{ fontSize: "14px" }}>{date}</Typography>
              <Typography sx={{ fontSize: "14px", color: "text.secondary" }}>
                {time}
              </Typography>
            </Box>
          );
        },
      },
      {
        key: "userRole",
        title: t("auditLogsPage.columns.role", "Role"),
        sortable: true,
        width: "95px",
      },
      {
        key: "actionDescription",
        title: t("auditLogsPage.columns.action", "Action"),
        sortable: true,
        width: "300px",
      },
      {
        key: "status",
        title: t("auditLogsPage.columns.actionsStatus", "Actions Status"),
        width: "140px",
        render: (value: unknown) => (
          <StatusBadge
            status={value as string}
            label={value === "success" ? "Success" : "Failure"}
          />
        ),
      },
      {
        key: "description",
        title: t("auditLogsPage.columns.description", "Description"),
        render: (value: unknown) => (
          <Typography sx={{ fontSize: "14px", lineHeight: 1.5 }}>
            {String(value)}
          </Typography>
        ),
      },
    ],
    [t, formatTimestamp],
  );

  return (
    <Layout>
      <AuditLogsPageStyled>
        <Box className="content-header">
          <Typography variant="h1">
            {t("auditLogsPage.title", "Audit Log")}
          </Typography>
          <Box className="filters-container">
            <Search
              width={320}
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={t(
                "auditLogsPage.searchPlaceholder",
                "Search by user, action type or patient",
              )}
            />
            <Box sx={{ position: "relative" }}>
              <Box sx={{ display: "flex", gap: "8px" }}>
                <DateInput
                  label=""
                  placeholder="From Date"
                  value={dateFrom}
                  onChange={handleDateFromChange}
                  width={155}
                  fullWidth={false}
                  error={!!dateRangeError}
                />
                <DateInput
                  label=""
                  placeholder="To Date"
                  value={dateTo}
                  onChange={handleDateToChange}
                  width={155}
                  fullWidth={false}
                  error={!!dateRangeError}
                />
              </Box>
              {dateRangeError && (
                <Typography
                  sx={{
                    position: "absolute",
                    top: "100%",
                    left: "2px",
                    fontSize: "12px",
                    color: "error.main",
                    whiteSpace: "nowrap",
                    mt: "2px",
                  }}
                >
                  {dateRangeError}
                </Typography>
              )}
            </Box>
            <Select
              label={t("auditLogsPage.filterByRole", "Filter by Role")}
              value={roleFilter}
              onChange={handleRoleFilterChange}
              options={ROLE_OPTIONS}
              width={200}
            />
          </Box>
        </Box>

        <Tabs tabs={TABS} activeTab={activeTab} onTabChange={handleTabChange} />

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              gap: 2,
            }}
          >
            <Typography color="error">{error}</Typography>
            <Button variant="outlined" onClick={() => fetchLogs()}>
              {t("auditLogsPage.retry", "Retry")}
            </Button>
          </Box>
        ) : (
          <DataTable<AuditLogRow>
            columns={columns}
            data={logRows}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            pageSize={ITEMS_PER_PAGE}
            currentPage={currentPage}
            totalCount={totalLogs}
            onPageChange={handlePageChange}
            emptyMessage={t("auditLogsPage.noResults", "No audit logs found")}
          />
        )}
      </AuditLogsPageStyled>
    </Layout>
  );
};

export default AuditLogsPage;
